import { useRef, useState, useMemo, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { ArrowUpRight, X, Star, Info, Linkedin, Twitter } from "lucide-react";
import { Link } from "react-router-dom";
import Section from "./Section";
import { resolveImageUrl } from "../admin/ImageUpload";
import LeaderModal from "./LeaderModal";

/* ===========================================================
   ProjectCard — fluid, alternating layout with scroll-linked
   image parallax + animated blob backdrop
   =========================================================== */
function ProjectCard({ project, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const imgY = useTransform(scrollYProgress, [0, 1], [-20, 20]);
  const blobX = useTransform(scrollYProgress, [0, 1], [-20, 20]);
  const isEven = index % 2 === 0;

  return (
    <motion.article
      ref={ref}
      style={{ y }}
      data-testid={`project-${index}`}
      className="relative grid lg:grid-cols-12 gap-8 lg:gap-12 items-center py-12"
    >
      <div className={`lg:col-span-7 ${isEven ? "lg:order-1" : "lg:order-2"}`}>
        <Link to={`/projects/${project.id}`} data-testid={`project-link-${project.id}`} aria-label={`Read about ${project.name}`} className="block group">
          <div className="relative">
            <motion.div
              style={{ x: blobX }}
              className={`blob ${index % 3 === 0 ? "blob-blue" : index % 3 === 1 ? "blob-cyan" : "blob-indigo"} -inset-8 z-0`}
            />
            <motion.div
              style={{ y: imgY }}
              className="relative z-10 overflow-hidden rounded-3xl border border-blue-100 bg-white p-2 shadow-[0_40px_80px_-40px_rgba(37,99,235,0.45)] transition-shadow group-hover:shadow-[0_60px_120px_-40px_rgba(37,99,235,0.55)]"
            >
              <div className="aspect-[16/10] rounded-2xl overflow-hidden">
                <img
                  src={resolveImageUrl(project.image_url)}
                  alt={project.name}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="absolute top-6 left-6 flex items-center gap-2">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white text-[#1E3A8A] text-xs font-semibold uppercase tracking-wider shadow-md">
                  {project.category}
                </div>
                {project.featured && (
                  <div className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-[#2563EB] text-white text-xs font-semibold uppercase tracking-wider shadow-md" data-testid="featured-badge">
                    <Star size={11} fill="currentColor" /> Featured
                  </div>
                )}
              </div>
              {project.metrics?.[0] && (
                <div className="absolute bottom-6 right-6 hidden md:flex items-center gap-2 glass-bright px-4 py-2 rounded-full">
                  <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs font-semibold text-[#0F172A]">{project.metrics[0]}</span>
                </div>
              )}
            </motion.div>
          </div>
        </Link>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7 }}
        className={`lg:col-span-5 ${isEven ? "lg:order-2" : "lg:order-1"}`}
      >
        <div className="font-mono-display text-xs text-[#2563EB]">
          {String(index + 1).padStart(2, "0")} / Project
        </div>
        <h3 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl font-semibold text-[#0F172A] leading-[1.05] text-balance">
          {project.name}
        </h3>
        {project.client && (
          <div className="mt-2 text-xs uppercase tracking-[0.18em] text-[#475569]">
            For · <span className="text-[#1E3A8A] font-semibold">{project.client}</span>
          </div>
        )}
        <p className="mt-5 text-[#475569] text-lg leading-relaxed">{project.summary}</p>

        {project.metrics?.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2">
            {project.metrics.map((m) => (
              <span key={m} className="px-3 py-1.5 rounded-md bg-blue-50 text-[#1D4ED8] text-xs font-semibold">{m}</span>
            ))}
          </div>
        )}

        {(project.problem || project.solution || project.impact) && (
          <div className="mt-7 space-y-3">
            {project.problem && <Detail label="Problem" value={project.problem} />}
            {project.solution && <Detail label="Solution" value={project.solution} />}
            {project.impact && <Detail label="Impact" value={project.impact} />}
          </div>
        )}

        <Link
          to={`/projects/${project.id}`}
          className="mt-8 inline-flex items-center gap-2 text-[#2563EB] font-semibold border-b-2 border-[#2563EB] pb-1 hover:gap-3 transition-all"
        >
          Read full case study <ArrowUpRight size={16} />
        </Link>
      </motion.div>
    </motion.article>
  );
}

const Detail = ({ label, value }) => (
  <div className="border-l-2 border-[#2563EB] pl-4">
    <div className="overline text-[10px]">{label}</div>
    <div className="mt-1 text-sm text-[#475569] leading-snug">{value}</div>
  </div>
);

const ALL = "All";
const FEATURED = "Featured";

export function ProjectsSection({ projects = [] }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get("tag") || (searchParams.get("featured") === "1" ? FEATURED : ALL);
  const [active, setActive] = useState(initial);

  useEffect(() => {
    const tag = searchParams.get("tag");
    if (tag) { setActive(tag); return; }
    if (searchParams.get("featured") === "1") { setActive(FEATURED); return; }
    setActive(ALL);
  }, [searchParams]);

  const tags = useMemo(() => {
    const set = new Set();
    projects.forEach((p) => (p.tags || []).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [projects]);

  const filtered = useMemo(() => {
    if (active === ALL) return projects;
    if (active === FEATURED) return projects.filter((p) => p.featured);
    return projects.filter((p) => (p.tags || []).includes(active));
  }, [active, projects]);

  const setFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    next.delete("tag");
    next.delete("featured");
    next.delete("category"); // legacy
    if (value === FEATURED) next.set("featured", "1");
    else if (value !== ALL) next.set("tag", value);
    setSearchParams(next, { replace: true });
  };

  const hasFeatured = projects.some((p) => p.featured);

  if (!projects.length) return null;

  return (
    <section id="projects" data-testid="section-projects" className="relative bg-white py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-dots-blue opacity-60 pointer-events-none" />
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[400px] wave-blue pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="section-overline">Success Stories</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight mt-5 text-balance text-[#0F172A]">
            Programmes that moved the needle for governments.
          </h2>
          <p className="mt-5 text-lg md:text-xl text-[#475569] leading-relaxed">
            A selection of platforms we have designed, built and scaled across taxation, urban governance,
            social justice, AI and handlooms — each delivering tangible citizen impact.
          </p>
        </motion.div>

        {/* Filter chips: Featured first, then All, then tags */}
        <div data-testid="project-filter" className="mt-12 flex flex-wrap gap-2 items-center">
          {hasFeatured && (
            <button
              onClick={() => setFilter(FEATURED)}
              data-testid="filter-featured"
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-all ${
                active === FEATURED
                  ? "bg-[#2563EB] text-white shadow-[0_10px_25px_-10px_rgba(37,99,235,0.6)]"
                  : "bg-blue-50 text-[#1D4ED8] hover:bg-blue-100"
              }`}
            >
              <Star size={12} fill={active === FEATURED ? "currentColor" : "none"} /> Featured
            </button>
          )}
          <button
            onClick={() => setFilter(ALL)}
            data-testid="filter-all"
            className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-all ${
              active === ALL
                ? "bg-[#2563EB] text-white shadow-[0_10px_25px_-10px_rgba(37,99,235,0.6)]"
                : "bg-blue-50 text-[#1D4ED8] hover:bg-blue-100"
            }`}
          >
            All
          </button>
          {tags.map((t) => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              data-testid={`filter-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-all ${
                active === t
                  ? "bg-[#2563EB] text-white shadow-[0_10px_25px_-10px_rgba(37,99,235,0.6)]"
                  : "bg-blue-50 text-[#1D4ED8] hover:bg-blue-100"
              }`}
            >
              {t}
            </button>
          ))}
          {active !== ALL && (
            <button
              onClick={() => setFilter(ALL)}
              className="inline-flex items-center gap-1 ml-2 text-xs text-[#475569] hover:text-[#2563EB]"
              data-testid="filter-clear"
            >
              <X size={12} /> Clear
            </button>
          )}
        </div>
        <div className="mt-3 text-sm text-[#475569]">
          Showing <span className="font-semibold text-[#0F172A]">{filtered.length}</span> {filtered.length === 1 ? "project" : "projects"}
          {active !== ALL && <> tagged <span className="font-semibold text-[#1E3A8A]">{active}</span></>}
        </div>

        <div className="mt-10 space-y-8 md:space-y-16">
          {filtered.length === 0 && (
            <div className="rounded-2xl border border-dashed border-blue-200 p-12 text-center text-[#475569]">
              No projects match this filter yet. <button onClick={() => setFilter(ALL)} className="text-[#2563EB] font-semibold underline">Show all</button>.
            </div>
          )}
          {filtered.map((p, i) => (
            <ProjectCard key={p.id || p.name} project={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

export function LeadershipSection({ leaders = [] }) {
  const [selected, setSelected] = useState(null);
  if (!leaders.length) return null;
  const board = leaders.filter((l) => (l.category || "board") !== "advisory");
  const advisory = leaders.filter((l) => l.category === "advisory");

  const renderCards = (group, groupKey) => (
    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {group.map((l, i) => (
        <motion.div
          key={l.id || l.name}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: i * 0.08 }}
          data-testid={`leader-${groupKey}-${i}`}
          className="group relative overflow-hidden rounded-xl bg-white dark:bg-[#0B1B40] border border-blue-100 dark:border-blue-800 hover:border-[#2563EB] hover:shadow-[0_24px_48px_-24px_rgba(37,99,235,0.3)] transition-all flex flex-col"
        >
          <div className="aspect-[4/5] overflow-hidden">
            <img src={resolveImageUrl(l.photo_url)} alt={l.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          </div>
          <div className="p-6 flex-1 flex flex-col">
            <div className="overline">{l.designation}</div>
            <div className="mt-2 font-display text-2xl text-[#0F172A] dark:text-white">{l.name}</div>
            <p className="mt-3 text-sm text-[#475569] dark:text-blue-100 leading-relaxed line-clamp-2">{l.bio}</p>
            <button
              onClick={() => setSelected(l)}
              data-testid={`leader-more-${groupKey}-${i}`}
              className="mt-4 inline-flex items-center gap-2 self-start text-xs font-semibold uppercase tracking-[0.18em] text-[#2563EB] dark:text-cyan-300 hover:gap-3 transition-all"
            >
              <Info size={13} /> More
            </button>
          </div>
        </motion.div>
      ))}
    </div>
  );

  return (
    <>
      {/* ================= LEADERSHIP → ADVISORY COMMITTEE ================= */}
      <section id="leadership" data-testid="section-leadership" className="relative bg-soft-blue dark:bg-[#0B1B40] py-24 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-grid-blue opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="section-overline">Leadership</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold mt-5 leading-tight text-balance text-[#0F172A] dark:text-white">
              The people behind GESIL&apos;s mission.
            </h2>
            <p className="mt-4 text-[#475569] dark:text-blue-100 text-base md:text-lg">
              Meet the Advisory Committee guiding our strategy and the Board of Directors stewarding day-to-day governance.
            </p>
          </div>

          {advisory.length > 0 && (
            <div className="mt-16">
              <div className="max-w-3xl">
                <div className="section-overline" style={{ color: "#FF9933" }}>Advisory Committee</div>
                <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold mt-4 leading-tight text-balance text-[#0F172A] dark:text-white">
                  Strategic guidance from industry stalwarts.
                </h3>
                <p className="mt-4 text-[#475569] dark:text-blue-100 text-base md:text-lg">
                  Distinguished advisors from technology, policy and governance shape our long-term direction.
                </p>
              </div>
              {renderCards(advisory, "advisory")}
            </div>
          )}
        </div>
      </section>

      {/* ================= BOARD OF DIRECTORS ================= */}
      <section id="board" data-testid="section-board" className="relative bg-white dark:bg-[#0F172A] py-24 md:py-28 overflow-hidden border-t border-blue-100 dark:border-blue-900">
        <div className="absolute -top-40 -right-32 h-96 w-96 rounded-full bg-blue-200/40 dark:bg-blue-900/40 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="section-overline">Board of Directors</div>
            <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold mt-4 leading-tight text-balance text-[#0F172A] dark:text-white">
              Stewards of GESIL&apos;s mission and craft.
            </h3>
            <p className="mt-4 text-[#475569] dark:text-blue-100 text-base md:text-lg">
              Our Board steers strategy, governance and delivery across every government and PSU engagement.
            </p>
          </div>
          {renderCards(board, "board")}
        </div>
      </section>

      <LeaderModal leader={selected} onClose={() => setSelected(null)} />
    </>
  );
}

export function BlogsSection({ blogs = [] }) {
  if (!blogs.length) return null;
  return (
    <Section
      id="blogs"
      eyebrow="Blogs"
      title="Notes from the frontiers of GovTech and Sovereign AI."
      subtitle="Field reports, research and frameworks from our practitioners working inside government departments."
    >
      <div className="grid md:grid-cols-3 gap-6 relative">
        <div className="absolute -top-10 -right-10 w-72 h-72 blob blob-cyan opacity-40 pointer-events-none" />
        {blogs.map((b, i) => (
          <motion.article
            key={b.id || b.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.06 }}
            data-testid={`blog-${i}`}
            className="group relative bg-white border border-blue-100 rounded-xl overflow-hidden hover:border-[#2563EB] hover:shadow-[0_24px_48px_-24px_rgba(37,99,235,0.25)] hover:-translate-y-1 transition-all"
          >
            <Link to={`/insights/${b.id}`} data-testid={`blog-link-${b.id}`} aria-label={`Read article ${b.title}`} className="absolute inset-0 z-10" />
            <div className="aspect-[16/10] overflow-hidden">
              <img src={resolveImageUrl(b.cover_image)} alt={b.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 text-xs uppercase tracking-[0.18em]">
                <span className="text-[#2563EB] font-semibold">{b.category}</span>
                <span className="text-blue-300">{b.published_at}</span>
              </div>
              <h3 className="mt-3 font-display text-xl font-semibold text-[#0F172A] leading-snug">{b.title}</h3>
              <p className="mt-3 text-sm text-[#475569] leading-relaxed line-clamp-3">{b.excerpt}</p>
              <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#0F172A] group-hover:text-[#2563EB]">
                Read article <ArrowUpRight size={14} />
              </div>
            </div>
          </motion.article>
        ))}
      </div>
    </Section>
  );
}

export function CareersSection({ careers = [] }) {
  return (
    <section id="careers" data-testid="section-careers" className="relative bg-gradient-to-b from-white to-blue-50 py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-hatch-blue opacity-70 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="section-overline">Careers</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold mt-5 leading-tight text-[#0F172A] text-balance">
            Build the future of public services with us.
          </h2>
          <p className="mt-6 text-[#475569] text-lg leading-relaxed max-w-md">
            Join engineers, designers, policy thinkers and AI researchers shaping the way India&apos;s
            governments deliver services to one billion citizens.
          </p>
          <a href="#contact" data-testid="careers-talent-pool" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-7 py-4 text-sm font-semibold uppercase tracking-wide shadow-[0_18px_40px_-18px_rgba(37,99,235,0.6)]">
            Join our Talent Pool <ArrowUpRight size={18} />
          </a>
        </div>
        <div className="lg:col-span-7 space-y-3">
          {careers.length === 0 && (
            <div className="rounded-xl border border-dashed border-blue-200 p-8 text-[#475569]">
              No active openings right now — connect with us to explore future roles.
            </div>
          )}
          {careers.map((c, i) => (
            <motion.div
              key={c.id || c.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              data-testid={`career-${i}`}
              className="group bg-white rounded-xl border border-blue-100 hover:border-[#2563EB] transition-colors p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-4 md:gap-6"
            >
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="px-2.5 py-1 rounded-md bg-blue-50 text-[#1D4ED8] font-semibold uppercase tracking-wider">{c.department}</span>
                  <span className="text-[#475569]">{c.location}</span>
                  <span className="text-blue-300">·</span>
                  <span className="text-[#475569]">{c.employment_type}</span>
                </div>
                <h3 className="mt-3 font-display text-xl font-semibold text-[#0F172A]">{c.title}</h3>
                <p className="mt-2 text-sm text-[#475569] line-clamp-2">{c.description}</p>
              </div>
              <a href="#contact" className="inline-flex items-center gap-2 rounded-full border border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white px-5 py-3 text-sm font-semibold uppercase tracking-wide transition-colors">
                Apply <ArrowUpRight size={16} />
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
